import { SearchIcon, X } from "lucide-react";
import { forwardRef, InputHTMLAttributes, Ref, useRef, useState } from "react";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "./ui/input-group";
import { Spinner } from "./ui/spinner";
import { cn } from "@/lib/utils";
import { mergeRefs } from "@/utils/mergeRef.utils";

type SearchCompProps = {
  placeholder?: string;
  isLoading?: boolean;
  value?: string;
  onValueChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  disabled?: boolean;
  defaultValue?: string;
  refocusOnClear?: boolean;
  className?: {
    inputClass?: string;
    buttonClass?: string;
    inputGroupClass?: string;
  };
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value" | "disabled" | "placeholder" | "className" | "type"
>;

const Search = (
  {
    placeholder = "Search...",
    isLoading = false,
    value: controlledValue,
    defaultValue = "",
    onValueChange,
    className,
    disabled = false,
    onSubmit,
    refocusOnClear = true,
    ...props
  }: SearchCompProps,
  ref: Ref<HTMLInputElement>
) => {
  const { inputClass, buttonClass, inputGroupClass } = className || {};

  // use states
  const [internalValue, setInternalValue] = useState<string>(defaultValue);

  //use ref
  const internalRef = useRef<HTMLInputElement | null>(null);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;
  const inputRef = mergeRefs(internalRef, ref);

  // event handlers
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onSubmit && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit(value);
    }
  };

  const handleClear = () => {
    if (!isControlled) {
      setInternalValue("");
    }
    onValueChange?.("");
    if (refocusOnClear && internalRef) {
      internalRef.current?.focus();
    }
  };

  return (
    <InputGroup data-disabled={disabled} className={cn("rounded-2xl transition-all duration-300 focus-within:ring-2 focus-within:ring-indigo-600/20", inputGroupClass)}>
      <InputGroupInput
        ref={inputRef}
        className={cn("bg-slate-50/50 border-none h-11 rounded-2xl px-5 text-sm font-medium focus-visible:ring-0", inputClass)}
        disabled={disabled}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        {...props}
      />
      <InputGroupAddon className="text-slate-400">
        <SearchIcon aria-hidden size={18} />
      </InputGroupAddon>
      <InputGroupAddon align="inline-end">
        {isLoading && <Spinner className="h-4 w-4" />}
        {value && !isLoading && (
          <InputGroupButton
            variant={"ghost"}
            className={cn(buttonClass, "hover:cursor-pointer text-slate-400 hover:text-indigo-600 hover:bg-indigo-50")}
            onClick={handleClear}
            aria-label="Clear search"
          >
            <X size={18} />
          </InputGroupButton>
        )}
      </InputGroupAddon>
    </InputGroup>
  );
};

const SearchComp = forwardRef(Search) as (
  props: SearchCompProps & { ref?: Ref<HTMLInputElement> }
) => ReturnType<typeof Search>;

export default SearchComp;

// note use useImperativeHandle for ref related things need to do research
